/**
 * Trace GraphQL Queries (7 total)
 *
 * Complete query coverage for Trace primitive:
 * 1. getTrace - Single trace by ID
 * 2. listTracesByWorkflowInstance - All traces for workflow instance
 * 3. listTracesByTaskId - All traces for a specific task
 * 4. listSpansByTraceId - All spans in a distributed trace
 * 5. getPerformanceStats - Performance metrics for workflow
 * 6. getAIDecisionStats - AI decision telemetry stats
 * 7. findBottlenecks - Identify slow operations
 */

import { gql } from '@apollo/client';
import {
  TRACE_FIELDS,
  TRACE_CONNECTION_FIELDS,
  PERFORMANCE_STATS_FIELDS,
  AI_DECISION_STATS_FIELDS,
} from './fragments';

/**
 * List all traces for tenant (paginated)
 */
export const LIST_TRACES = gql`
  ${TRACE_CONNECTION_FIELDS}
  query ListTraces($tenantId: String!, $limit: Int, $cursor: String) {
    listTraces(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
      ...TraceConnectionFields
    }
  }
`;

/**
 * Get trace by ID
 */
export const GET_TRACE = gql`
  ${TRACE_FIELDS}
  query GetTrace($tenantId: String!, $traceId: String!) {
    getTrace(tenantId: $tenantId, traceId: $traceId) {
      ...TraceFields
    }
  }
`;

/**
 * List traces by workflow instance
 */
export const LIST_TRACES_BY_WORKFLOW_INSTANCE = gql`
  ${TRACE_CONNECTION_FIELDS}
  query ListTracesByWorkflowInstance(
    $tenantId: String!
    $workflowInstanceId: String!
    $limit: Int
    $cursor: String
  ) {
    listTracesByWorkflowInstance(
      tenantId: $tenantId
      workflowInstanceId: $workflowInstanceId
      limit: $limit
      cursor: $cursor
    ) {
      ...TraceConnectionFields
    }
  }
`;

/**
 * List traces by task ID
 */
export const LIST_TRACES_BY_TASK_ID = gql`
  ${TRACE_CONNECTION_FIELDS}
  query ListTracesByTaskId($tenantId: String!, $taskId: String!, $limit: Int) {
    listTracesByTaskId(tenantId: $tenantId, taskId: $taskId, limit: $limit) {
      ...TraceConnectionFields
    }
  }
`;

/**
 * List spans by trace ID (distributed tracing)
 */
export const LIST_SPANS_BY_TRACE_ID = gql`
  ${TRACE_FIELDS}
  query ListSpansByTraceId($tenantId: String!, $traceId: String!) {
    listSpansByTraceId(tenantId: $tenantId, traceId: $traceId) {
      ...TraceFields
    }
  }
`;

/**
 * Get performance statistics for workflow
 */
export const GET_PERFORMANCE_STATS = gql`
  ${PERFORMANCE_STATS_FIELDS}
  query GetPerformanceStats($tenantId: String!, $workflowInstanceId: String!) {
    getPerformanceStats(tenantId: $tenantId, workflowInstanceId: $workflowInstanceId) {
      ...PerformanceStatsFields
    }
  }
`;

/**
 * Get AI decision statistics
 */
export const GET_AI_DECISION_STATS = gql`
  ${AI_DECISION_STATS_FIELDS}
  query GetAIDecisionStats($tenantId: String!, $workflowInstanceId: String!) {
    getAIDecisionStats(tenantId: $tenantId, workflowInstanceId: $workflowInstanceId) {
      ...AIDecisionStatsFields
    }
  }
`;

/**
 * Find performance bottlenecks
 */
export const FIND_BOTTLENECKS = gql`
  ${TRACE_FIELDS}
  query FindBottlenecks($tenantId: String!, $workflowInstanceId: String!, $threshold: Int!) {
    findBottlenecks(
      tenantId: $tenantId
      workflowInstanceId: $workflowInstanceId
      threshold: $threshold
    ) {
      ...TraceFields
    }
  }
`;
